// The Apps page's forms, each in the page's place (01-the-console.md §
// conventions: a form; § the moves of 2026-11-01): *Connect an app*, whose
// last step is what the person leaves with — the key and the API endpoint,
// shown once; *New key*'s key, shown the same way; and *Use your AI client*
// (02-DESIGN/09-gateway/02-the-oauth-face.md § what the console shows): the
// toolkit's address with the workspace in it, the one line for the common
// clients, and the sentence that the client opens the browser on the
// workspace's sign-in — no key, because none exists.

import { useState } from "react";

import {
  Button,
  Choices,
  Field,
  NotAllowed,
  Notice,
  OnceValue,
  Select,
  TextInput,
  useLoad,
} from "../../ui/components.tsx";
import { FormPage } from "../../ui/form.tsx";
import { AppKind, Subjects, type AppCredentialReply, type AppRegisterRequest } from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import { useSurface } from "../surface.tsx";
import { AN_APP, addLines, clientSignsIn, personAddress, TOOLKIT_PLACEHOLDER } from "./desktop.ts";

/**
 * ConnectApp is *Connect an app*: the name and what it acts as. Once the app
 * exists, `onConnected` hands its key to the page, whose key step — the key
 * and the API endpoint, shown once — takes the form's place.
 */
export function ConnectApp({
  onClose,
  onConnected,
}: {
  onClose: () => void;
  onConnected: (reply: AppCredentialReply) => void;
}) {
  const s = useSurface();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<string>(AppKind.personal);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const asWorkspace = s.offered("apps:register:tenant");
  const submit = () => {
    if (busy || name.trim() === "") {
      return;
    }
    setBusy(true);
    setErr(undefined);
    const body: AppRegisterRequest = { name: name.trim(), kind };
    s.bus.call<AppCredentialReply>(SERVICE, Subjects.appsRegister(), body).then(
      (r) => {
        setBusy(false);
        onConnected(r);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  return (
    <FormPage
      title="Connect an app"
      line="A program built against the OpenAI API, the Anthropic API or MCP. You leave with a key and the API endpoint to paste into it."
      back="Apps"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || name.trim() === ""}>
            {busy ? "Connecting…" : "Connect"}
          </Button>
        </>
      }
    >
      {err !== undefined && <NotAllowed err={err} what="Connect" />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="Name" hint="What you call it. Connecting again under the same name makes a second app.">
          <TextInput value={name} onChange={setName} placeholder="CoolApp" required autoFocus />
        </Field>
        <div className="field">
          <span className="label">Acts as</span>
          <Choices
            name="kind"
            value={kind}
            onChange={setKind}
            options={[
              { value: AppKind.personal, label: "You", hint: "The app does what you may do, as you." },
              {
                value: AppKind.tenant,
                label: s.name,
                hint: asWorkspace
                  ? `The app acts for ${s.name}, with you as the person who connected it.`
                  : "An admin connects an app that acts for the workspace.",
                disabled: !asWorkspace,
              },
            ]}
          />
        </div>
      </form>
    </FormPage>
  );
}

/**
 * KeyForm is the last step of *Connect an app* and of *New key*: the API
 * endpoint — the gateway's base URL the platform profile publishes as
 * `gateway_url`, named as absent on an install that publishes none — and the
 * key, shown now and never again.
 */
export function KeyForm({
  reply,
  fresh,
  gateway,
  back = "Apps",
  onClose,
}: {
  reply: AppCredentialReply;
  fresh: boolean;
  gateway: string | undefined;
  /** What the form stands in front of: the list, or the app's page. */
  back?: string;
  onClose: () => void;
}) {
  const name = reply.app.name;
  return (
    <FormPage
      title={fresh ? `${name} is connected` : `A new key for ${name}`}
      line={`Paste these into ${name}. The key is shown now and never again; if you lose it, make a new one here.`}
      back={back}
      onClose={onClose}
      foot={
        <Button kind="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      {gateway !== undefined ? (
        <OnceValue label="API endpoint" value={gateway} />
      ) : (
        <Notice title="No API endpoint yet">
          This install does not publish one; the key alone is below.
        </Notice>
      )}
      <OnceValue label="Key" value={reply.credential} />
      <p className="small faint">
        {name} takes them as its API base URL and API key. An Anthropic-compatible app takes the key as{" "}
        <span className="mono">x-api-key</span>; an MCP client as a bearer token.
      </p>
    </FormPage>
  );
}

/** DesktopClient is *Use your AI client*: a toolkit picked, its address and the one line to add it. */
export function DesktopClient({ gateway, onClose }: { gateway: string | undefined; onClose: () => void }) {
  const s = useSurface();
  const [toolkits] = useLoad("toolkits", () => s.bus.discoverToolkits());
  const [picked, setPicked] = useState("");
  const names =
    toolkits.state === "ready" ? [...new Set(toolkits.value.map((t) => t.name))].sort() : ([] as string[]);
  const toolkit = picked === "" ? TOOLKIT_PLACEHOLDER : picked;
  const address = gateway === undefined ? undefined : personAddress(gateway, s.tenant, toolkit);
  return (
    <FormPage
      title="Use your AI client"
      line={`For Claude Code, Claude Desktop or any client that signs in on its own: give it a toolkit's address, and it signs in as you. ${AN_APP}`}
      back="Apps"
      onClose={onClose}
      foot={
        <Button kind="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <Field
        label="Toolkit"
        hint={
          toolkits.state === "ready" && names.length === 0
            ? `${s.name} offers no toolkits yet; the address below has a stand-in for one.`
            : "The toolkit your client should call."
        }
      >
        <Select
          value={picked}
          onChange={setPicked}
          disabled={toolkits.state !== "ready" || names.length === 0}
          options={[{ value: "", label: "Pick a toolkit" }, ...names.map((n) => ({ value: n, label: n }))]}
        />
      </Field>
      {toolkits.state === "refused" && <NotAllowed err={toolkits.err} what="Toolkits" />}
      {address === undefined ? (
        <Notice title="No address yet">
          This install publishes no address for its AI clients, so there is nothing to add yet.
        </Notice>
      ) : (
        <>
          <div className="field">
            <span className="label">Address</span>
            <span className="mono small">{address}</span>
          </div>
          {addLines(toolkit, address).map((l) => (
            <OnceValue key={l.client} label={`The line for ${l.client}`} value={l.line} />
          ))}
          <p className="small faint">
            A client that adds a connector by its address, such as Claude Desktop, takes the address alone:
            paste it and sign in.
          </p>
          <Notice>{clientSignsIn(s.name)}</Notice>
        </>
      )}
    </FormPage>
  );
}
